import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Download,
  Settings2,
  Search,
  X,
  Filter,
  TrendingUp,
  TrendingDown,
  Hash,
  ArrowUpDown,
  ChevronDown as ChevronDownIcon,
  Loader2,
} from 'lucide-react';
import { KPI } from '@/components/ui/kpi';
import { CompactKpiCard } from '@/components/kpi-card';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import { SensitiveData } from '@/components/sensitive-data';
import { cn, formatCurrency } from '@/lib/utils';

import { TransactionGroupByFilter, type TransactionGroupById } from '@/components/transaction-group-by-filter';

import { ColumnConfiguratorDrawer, type ColumnDefinition } from '@/components/column-configurator-drawer';
import { useColumnPreferences } from '@/hooks/use-column-preferences';
import { usePagination } from '@/hooks/use-pagination';
import { calculatePagination, type PaginationMetadata } from '@/types/pagination';
import { PaginationControls } from '@/components/pagination-controls';
import { getDataMode } from '@/lib/data-service';
import { queryProjectTransactions, queryProjectTransactionTotals, queryProjectTransactionGroupedTotals, fetchProjects, fetchEmployees, fetchClients, type ProjectTransactionDto, type ProjectTransactionQueryBody, type ProjectTransactionTotalsResponse, type ProjectTransactionGroupedTotalsResponse } from '@/lib/data-service';
import { PAGE_SIZE_OPTIONS } from '@/types/pagination';
import { TransactionFilterSheet, countActiveTransactionFilters, EMPTY_TRANSACTION_FILTERS, type TransactionFilters } from '@/components/transaction-filter-sheet';
import { exportTransactionsToExcel, fetchAllTransactionPages } from '@/lib/export-transactions-excel';
import { TransactionsGrid, type CostingTransactionHeader } from '@/components/transactions-grid';
import type { ColumnSort } from '@/components/ui/data-table-enhanced';

// Use the same page size options as the Projects List grid (25, 50, 100)
const REPORT_PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS;

// Maps FE column IDs to BE sort field names (matching TransactionQueryRequest / SortFieldMap)
const SORT_FIELD_MAP: Record<string, string> = {
  projectCode: 'ProjectCode',
  projectName: 'ProjectName',
  date: 'Date',
  transactionType: 'TransactionType',
  origin: 'Source',
  resourceCode: 'ResourceCode',
  resourceName: 'ResourceName',
  employeeCode: 'EmployeeCode',
  accountCode: 'AccountCode',
  accountName: 'AccountName',
  costCentreCode: 'CostCentreCode',
  costCentreName: 'CostCentreName',
  quantity: 'Quantity',
  price: 'Price',
  value: 'Value',
  actualSales: 'ActualSales',
  actualCost: 'ActualCost',
  currencyCode: 'CurrencyCode',
  batched: 'IsBatched',
  isInvoiced: 'IsInvoiced',
  postType: 'EntryType',
  projectStatus: 'ProjectStatus',
  periodNumber: 'PeriodNumber',
  yearLabel: 'YearLabel',
  weekNo: 'WeekNo',
  detail: 'Detail',
  headerRef: 'Reference',
  internalRef: 'TransactionId',
  costingSortKey: 'SortKey',
  enteredBy: 'EnteredBy',
  dateEntered: 'DateEntered',
  batchRef: 'BatchRef',
  nominalCode: 'NominalCode',
};

// Transaction types (needed for mock data generation)
type TransactionType =
  | 'NotSet'
  | 'Timesheet'
  | 'Invoice'
  | 'CreditNote'
  | 'Sales'
  | 'Journal'
  | 'TaxJournal'
  | 'DebitAdjustment'
  | 'CreditAdjustment'
  | 'ReceiptPayment'
  | 'Miscellaneous'
  | 'CurrencyTransfer'
  | 'StockManagement';

type TransactionOrigin =
  | 'NotSet'
  | 'Costing'
  | 'PurchaseLedger'
  | 'SalesLedger'
  | 'NominalLedger'
  | 'Stock';

type PostType = 'C' | 'I' | 'S'; // Cost, Income, Sales

// Filter state — uses the TransactionFilters type from the sheet component
type FilterState = TransactionFilters;

// ── Mock data generator ──────────────────────────────────────────────
// Generates ~500 realistic project transactions spanning 12 months so demo mode
// exercises pagination, filtering, and sorting with a meaningful dataset.

const MOCK_PROJECTS = [
  { code: 'PROJ001', name: 'Website Redesign', status: 'Active', client: { code: 'CLI001', name: 'Acme Corporation Ltd' } },
  { code: 'PROJ002', name: 'Mobile App Development', status: 'Active', client: { code: 'CLI002', name: 'TechStart Inc' } },
  { code: 'PROJ003', name: 'Marketing Campaign Q2', status: 'Active', client: { code: 'CLI003', name: 'Global Brands Ltd' } },
  { code: 'PROJ004', name: 'Data Analytics Platform', status: 'Active', client: { code: 'CLI004', name: 'DataCorp PLC' } },
  { code: 'PROJ005', name: 'ERP Integration', status: 'Active', client: { code: 'CLI005', name: 'Enterprise Solutions Ltd' } },
  { code: 'PROJ006', name: 'Cloud Migration', status: 'Active', client: { code: 'CLI006', name: 'CloudFirst Systems' } },
  { code: 'PROJ007', name: 'Security Audit', status: 'Complete', client: { code: 'CLI007', name: 'SecureNet Partners' } },
  { code: 'PROJ008', name: 'HR Portal Revamp', status: 'Active', client: { code: 'CLI008', name: 'PeopleFirst HR' } },
  { code: 'PROJ009', name: 'Supply Chain Optimisation', status: 'On Hold', client: { code: 'CLI009', name: 'LogiChain UK' } },
  { code: 'PROJ010', name: 'AI Chatbot Development', status: 'Active', client: { code: 'CLI010', name: 'SmartTalk AI' } },
  { code: 'PROJ011', name: 'Financial Reporting Suite', status: 'Active', client: { code: 'CLI011', name: 'FinView Analytics' } },
  { code: 'PROJ012', name: 'Customer Portal v2', status: 'Active', client: { code: 'CLI012', name: 'RetailMax Group' } },
] as const;

const MOCK_RESOURCES = [
  { code: 'RES001', name: 'John Smith', empCode: 'EMP001', rate: 85 },
  { code: 'RES002', name: 'Sarah Johnson', empCode: 'EMP002', rate: 92 },
  { code: 'RES003', name: 'Emily Davis', empCode: 'EMP003', rate: 75 },
  { code: 'RES004', name: 'James Wilson', empCode: 'EMP004', rate: 110 },
  { code: 'RES005', name: 'Mike Thompson', empCode: 'EMP005', rate: 95 },
  { code: 'RES006', name: 'Lisa Chen', empCode: 'EMP006', rate: 88 },
  { code: 'RES007', name: 'David Brown', empCode: 'EMP007', rate: 78 },
  { code: 'RES008', name: 'Rachel Green', empCode: 'EMP008', rate: 105 },
  { code: 'RES009', name: 'Tom Harris', empCode: 'EMP009', rate: 70 },
  { code: 'RES010', name: 'Amy Porter', empCode: 'EMP010', rate: 98 },
] as const;

const MOCK_COST_CENTRES = [
  { code: 'CC100', name: 'Development' },
  { code: 'CC200', name: 'Infrastructure' },
  { code: 'CC300', name: 'Marketing' },
  { code: 'CC400', name: 'Travel' },
  { code: 'CC500', name: 'Consulting' },
  { code: 'CC600', name: 'Design' },
  { code: 'CC700', name: 'Project Management' },
  { code: 'CC800', name: 'Quality Assurance' },
] as const;

const MOCK_ANALYSIS = [
  { code: 'DEV', name: 'Development', nominal: '4000' },
  { code: 'INF', name: 'Infrastructure', nominal: '5000' },
  { code: 'MKT', name: 'Marketing', nominal: '3000' },
  { code: 'TRV', name: 'Travel', nominal: '6100' },
  { code: 'CON', name: 'Consulting', nominal: '4500' },
  { code: 'DES', name: 'Design', nominal: '4200' },
  { code: 'QA', name: 'Quality Assurance', nominal: '4000' },
  { code: 'PM', name: 'Project Management', nominal: '4100' },
] as const;

// Transaction type definitions with associated origin, ref prefix, and post type weights
const MOCK_TX_TYPES: Array<{
  type: TransactionType;
  origin: TransactionOrigin;
  refPrefix: string;
  postTypes: PostType[];
  hasResource: boolean;
  details: string[];
}> = [
  {
    type: 'Timesheet', origin: 'Costing', refPrefix: 'TS', postTypes: ['C'],
    hasResource: true,
    details: ['Frontend development', 'Backend API work', 'Database design', 'Code review', 'Architecture planning',
              'Bug fixing', 'Unit testing', 'Integration testing', 'Documentation', 'Sprint planning',
              'Client workshop', 'Requirements gathering', 'Performance tuning', 'DevOps pipeline setup'],
  },
  {
    type: 'Invoice', origin: 'PurchaseLedger', refPrefix: 'PO', postTypes: ['C'],
    hasResource: false,
    details: ['Cloud hosting services', 'Software licences', 'Hardware procurement', 'Third-party API subscription',
              'SSL certificates', 'Domain renewal', 'Database hosting', 'CDN services', 'Monitoring tools',
              'CI/CD platform licence', 'Design tool subscription', 'Security scanning tools'],
  },
  {
    type: 'Miscellaneous', origin: 'Costing', refPrefix: 'EXP', postTypes: ['C'],
    hasResource: true,
    details: ['Client meeting - Travel', 'Conference attendance', 'Team lunch', 'Office supplies',
              'Client site visit', 'Training course', 'Parking & tolls', 'Hotel accommodation',
              'Flight booking', 'Taxi / ride-share', 'Working lunch with stakeholders'],
  },
  {
    type: 'Sales', origin: 'SalesLedger', refPrefix: 'INV', postTypes: ['S'],
    hasResource: false,
    details: ['Monthly retainer', 'Milestone payment', 'Phase completion invoice', 'Time & materials invoice',
              'Change request billing', 'Support contract renewal', 'Consultancy fees', 'Training delivery'],
  },
  {
    type: 'DebitAdjustment', origin: 'NominalLedger', refPrefix: 'ADJ', postTypes: ['C'],
    hasResource: false,
    details: ['Reallocation - Travel expenses', 'Cost centre correction', 'Period adjustment',
              'Accrual reversal', 'Intercompany recharge', 'Budget reallocation'],
  },
  {
    type: 'CreditAdjustment', origin: 'NominalLedger', refPrefix: 'CRD', postTypes: ['C', 'S'],
    hasResource: false,
    details: ['Credit note - overcharge', 'Duplicate reversal', 'Discount applied', 'Goodwill credit',
              'Early payment discount', 'Volume discount adjustment'],
  },
];

const MOCK_ENTERED_BY = ['ADMIN', 'FINANCE', 'JSMITH', 'SJOHNSON', 'EDAVIS', 'JWILSON', 'MTHOMPSON', 'LCHEN'];

/**
 * Simple seeded PRNG (mulberry32) so mock data is deterministic across renders.
 */
function createSeededRng(seed: number) {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function generateMockTransactions(count: number): CostingTransactionHeader[] {
  const rng = createSeededRng(42);
  const pick = <T,>(arr: readonly T[]): T => arr[Math.floor(rng() * arr.length)];
  const pickIdx = (max: number) => Math.floor(rng() * max);
  const round2 = (n: number) => Math.round(n * 100) / 100;

  // Generate dates spread over ~12 months (Jan 2025 - Dec 2025)
  const startDate = new Date('2025-01-06');
  const endDate = new Date('2025-12-19');
  const dateRange = endDate.getTime() - startDate.getTime();

  const transactions: CostingTransactionHeader[] = [];

  for (let i = 0; i < count; i++) {
    const project = pick(MOCK_PROJECTS);
    const txDef = pick(MOCK_TX_TYPES);
    const cc = pick(MOCK_COST_CENTRES);
    const analysis = pick(MOCK_ANALYSIS);
    const postType = pick(txDef.postTypes);
    const detail = pick(txDef.details);
    const enteredBy = pick(MOCK_ENTERED_BY);

    // Resource - only populated for types that have one
    const resource = txDef.hasResource ? pick(MOCK_RESOURCES) : null;

    // Date
    const txTimestamp = startDate.getTime() + rng() * dateRange;
    const txDate = new Date(txTimestamp);
    const yyyy = txDate.getFullYear();
    const mm = String(txDate.getMonth() + 1).padStart(2, '0');
    const dd = String(txDate.getDate()).padStart(2, '0');
    const dateStr = `${yyyy}-${mm}-${dd}`;

    // Week / period
    const dayOfYear = Math.floor((txDate.getTime() - new Date(yyyy, 0, 1).getTime()) / 86400000);
    const weekNo = Math.floor(dayOfYear / 7) + 1;
    const periodNumber = txDate.getMonth() + 1;

    // Financial values
    const isSales = postType === 'S';
    const baseQty = txDef.type === 'Timesheet'
      ? [1, 2, 3, 4, 5, 6, 7, 8, 10, 12, 16][pickIdx(11)]
      : txDef.type === 'Sales'
        ? 1
        : [1, 1, 1, 2, 3, 5, 10][pickIdx(7)];
    const basePrice = isSales
      ? [2500, 5000, 7500, 8500, 10000, 12000, 15000, 20000, 25000][pickIdx(9)]
      : resource
        ? resource.rate + round2((rng() - 0.5) * 20)
        : [45, 85, 120, 250, 380, 450, 650, 950, 1250, 1800, 2500][pickIdx(11)];
    const quantity = baseQty;
    const price = round2(basePrice);
    const value = round2(quantity * price);
    const upliftPct = resource && !isSales ? (rng() > 0.5 ? [10, 15, 20, 25][pickIdx(4)] : 0) : 0;
    const upliftValue = round2(value * upliftPct / 100);
    const priceWithUplift = round2(price * (1 + upliftPct / 100));
    const actualSales = isSales ? value : (upliftPct > 0 ? round2(value + upliftValue) : 0);
    const actualCost = isSales ? 0 : value;

    // Batched / invoiced - sales invoices are more likely batched & invoiced
    const batched = isSales ? rng() > 0.2 : rng() > 0.65;
    const isInvoiced = isSales ? (batched && rng() > 0.3) : rng() > 0.85;

    // Refs
    const refNum = String(1000 + i).padStart(4, '0');
    const headerRef = `${txDef.refPrefix}-${yyyy}-${refNum}`;
    const internalRef = 100000 + i;

    // Supplier / invoice accounts
    const supplierAccountCode = txDef.origin === 'PurchaseLedger' ? `SUP${String(pickIdx(20) + 1).padStart(3, '0')}` : '';
    const invoiceAccountCode = isSales || txDef.type === 'Timesheet' ? project.client.code : '';
    const invoiceAccountName = invoiceAccountCode ? project.client.name : '';

    // Date entered (1-3 days after transaction date)
    const enteredOffset = Math.floor(rng() * 3);
    const enteredDate = new Date(txDate.getTime() + enteredOffset * 86400000);
    const enteredHour = 8 + Math.floor(rng() * 10);
    const enteredMin = Math.floor(rng() * 60);
    const dateEntered = `${enteredDate.getFullYear()}-${String(enteredDate.getMonth() + 1).padStart(2, '0')}-${String(enteredDate.getDate()).padStart(2, '0')}T${String(enteredHour).padStart(2, '0')}:${String(enteredMin).padStart(2, '0')}:00`;

    // Batch ref
    const batchRef = batched ? `BATCH-${yyyy}-${mm}` : '';

    // Sales order number
    const salesOrderNumber = isSales && isInvoiced ? 10000 + Math.floor(rng() * 5000) : 0;
    const purchaseOrderNumber = txDef.origin === 'PurchaseLedger' ? headerRef : '';

    transactions.push({
      projectCode: project.code,
      projectName: project.name,
      date: dateStr,
      transactionType: txDef.type,
      origin: txDef.origin,
      detail: `${detail}${txDef.type === 'Timesheet' ? ` - ${quantity} hours` : ''}`,
      headerRef,
      internalRef,
      accountCode: project.client.code,
      accountName: project.client.name,
      costCentreCode: cc.code,
      costCentreName: cc.name,
      invoiceAccountCode,
      invoiceAccountName,
      supplierAccountCode,
      resourceCode: resource?.code ?? '',
      resourceName: resource?.name ?? '',
      employeeCode: resource?.empCode ?? '',
      quantity,
      price,
      value,
      uplift: upliftPct,
      upliftValue,
      priceWithUplift,
      actualSales,
      actualCost,
      currencyCode: rng() > 0.92 ? 'EUR' : rng() > 0.85 ? 'USD' : 'GBP',
      postType,
      projectStatus: project.status,
      batched,
      isInvoiced,
      periodNumber,
      yearLabel: String(yyyy),
      weekNo,
      costingSortKey: `${project.code}-${txDef.refPrefix}`,
      salesOrderNumber,
      purchaseOrderNumber,
      batchRef,
      enteredBy,
      dateEntered,
      costingUserField1: '',
      costingUserField2: '',
      costingUserField3: '',
      costingUserField4: '',
      costingUserField5: '',
      costingUserField6: '',
      userDate1: '',
      userDate2: '',
      userChar1: '',
      userChar2: '',
      userChar3: '',
      userChar4: '',
      userFlag1: false,
      userFlag2: false,
      userNumber1: 0,
      userNumber2: 0,
      userKey1: '',
      userKey2: '',
      userKey3: '',
      stockCode: '',
      stockName: '',
      analysisCode: analysis.code,
      analysisName: analysis.name,
      nominalCode: analysis.nominal,
    });
  }

  // Sort by date descending (newest first) like the live API default
  transactions.sort((a, b) => b.date.localeCompare(a.date) || b.internalRef - a.internalRef);
  return transactions;
}

// Generate 500 realistic mock transactions for demo mode
const MOCK_TRANSACTIONS: CostingTransactionHeader[] = generateMockTransactions(500);

// Column definitions for the 47 header fields
const COLUMN_DEFINITIONS: ColumnDefinition[] = [
  // Core columns (12 default visible)
  { id: 'projectCode', name: 'Project Code', description: 'Project identifier', category: 'Status', visible: true, order: 0 },
  { id: 'projectName', name: 'Project Name', description: 'Project name', category: 'Status', visible: true, locked: true, order: 1 },
  { id: 'accountCode', name: 'Account Code', description: 'Client/account code', category: 'Client', visible: true, order: 2 },
  { id: 'accountName', name: 'Account Name', description: 'Client/account name', category: 'Client', visible: true, order: 3 },
  { id: 'date', name: 'Date', description: 'Transaction date', category: 'Timeline', visible: true, order: 4 },
  { id: 'transactionType', name: 'Type', description: 'Transaction type', category: 'Status', visible: true, order: 5 },
  { id: 'origin', name: 'Origin', description: 'Transaction origin system', category: 'Status', visible: true, order: 6 },
  { id: 'resourceName', name: 'Resource', description: 'Resource name', category: 'Resource', visible: true, order: 7 },
  { id: 'quantity', name: 'Quantity', description: 'Transaction quantity', category: 'Financial', visible: true, order: 8, align: 'right' },
  { id: 'price', name: 'Price', description: 'Unit price', category: 'Financial', visible: true, order: 9, align: 'right' },
  { id: 'value', name: 'Value', description: 'Transaction value', category: 'Financial', visible: true, order: 10, align: 'right' },
  { id: 'batched', name: 'Batched', description: 'Batched status', category: 'Status', visible: true, order: 11 },
  
  // Financial columns (additional)
  { id: 'uplift', name: 'Uplift %', description: 'Uplift percentage', category: 'Financial', visible: false, order: 12, align: 'right' },
  { id: 'upliftValue', name: 'Uplift Value', description: 'Uplift amount', category: 'Financial', visible: false, order: 13, align: 'right' },
  { id: 'priceWithUplift', name: 'Price + Uplift', description: 'Price including uplift', category: 'Financial', visible: false, order: 14, align: 'right' },
  { id: 'valueWithUplift', name: 'Value + Uplift', description: 'Value including uplift (computed)', category: 'Financial', visible: false, order: 15, align: 'right' },
  { id: 'actualSales', name: 'Actual Sales', description: 'Actual sales amount', category: 'Financial', visible: false, order: 16, align: 'right' },
  { id: 'actualCost', name: 'Actual Cost', description: 'Actual cost amount', category: 'Financial', visible: false, order: 17, align: 'right' },
  { id: 'currencyCode', name: 'Currency', description: 'Currency code', category: 'Financial', visible: false, order: 18 },
  
  // Resource columns
  { id: 'resourceCode', name: 'Resource Code', description: 'Resource identifier', category: 'Resource', visible: false, order: 19 },
  { id: 'employeeCode', name: 'Employee Code', description: 'Employee identifier', category: 'Resource', visible: false, order: 20 },
  
  // Account columns
  { id: 'invoiceAccountCode', name: 'Invoice Account Code', description: 'Invoice account code', category: 'Client', visible: false, order: 21 },
  { id: 'invoiceAccountName', name: 'Invoice Account Name', description: 'Invoice account name', category: 'Client', visible: false, order: 22 },
  { id: 'supplierAccountCode', name: 'Supplier Code', description: 'Supplier account code', category: 'Client', visible: false, order: 23 },
  
  // Status columns
  { id: 'postType', name: 'Post Type', description: 'Post type (C/I/S)', category: 'Status', visible: false, order: 24 },
  { id: 'projectStatus', name: 'Project Status', description: 'Project status', category: 'Status', visible: false, order: 25 },
  { id: 'isInvoiced', name: 'Invoiced', description: 'Invoiced status', category: 'Status', visible: false, order: 26 },
  
  // Period columns
  { id: 'periodNumber', name: 'Period', description: 'Period number', category: 'Timeline', visible: false, order: 27, align: 'right' },
  { id: 'yearLabel', name: 'Year', description: 'Year label', category: 'Timeline', visible: false, order: 28 },
  { id: 'weekNo', name: 'Week', description: 'Week number', category: 'Timeline', visible: false, order: 29, align: 'right' },
  
  // Reference columns
  { id: 'detail', name: 'Detail', description: 'Transaction detail', category: 'Status', visible: false, order: 30 },
  { id: 'headerRef', name: 'Header Ref', description: 'Header reference', category: 'Status', visible: false, order: 31 },
  { id: 'internalRef', name: 'Internal Ref', description: 'Internal reference', category: 'Status', visible: false, order: 32, align: 'right' },
  { id: 'costCentreCode', name: 'Cost Centre Code', description: 'Cost centre code', category: 'Financial', visible: false, order: 33 },
  { id: 'costCentreName', name: 'Cost Centre', description: 'Cost centre name', category: 'Financial', visible: false, order: 34 },
  { id: 'costingSortKey', name: 'Costing Sort Key', description: 'Costing sort key', category: 'Status', visible: false, order: 35 },
  { id: 'salesOrderNumber', name: 'Sales Order', description: 'Sales order number', category: 'Status', visible: false, order: 36, align: 'right' },
  { id: 'purchaseOrderNumber', name: 'Purchase Order', description: 'Purchase order number', category: 'Status', visible: false, order: 37 },
  { id: 'batchRef', name: 'Batch Ref', description: 'Batch reference', category: 'Status', visible: false, order: 38 },
  { id: 'enteredBy', name: 'Entered By', description: 'User who entered', category: 'Status', visible: false, order: 39 },
  { id: 'dateEntered', name: 'Date Entered', description: 'Date entered', category: 'Timeline', visible: false, order: 40 },
  { id: 'stockCode', name: 'Stock Code', description: 'Stock code', category: 'Status', visible: false, order: 41 },
  { id: 'stockName', name: 'Stock Name', description: 'Stock name', category: 'Status', visible: false, order: 42 },
  { id: 'analysisCode', name: 'Analysis Code', description: 'Analysis code', category: 'Financial', visible: false, order: 43 },
  { id: 'analysisName', name: 'Analysis', description: 'Analysis name', category: 'Financial', visible: false, order: 44 },
  { id: 'nominalCode', name: 'Nominal Code', description: 'Nominal code', category: 'Financial', visible: false, order: 45 },
];

// Column presets
const COLUMN_PRESETS = [
  {
    id: 'default',
    name: 'Default View',
    columns: COLUMN_DEFINITIONS.filter(c => c.visible).map(c => ({ id: c.id, visible: true })),
  },
  {
    id: 'finance',
    name: 'Finance View',
    columns: [
      { id: 'projectCode', visible: true },
      { id: 'projectName', visible: true },
      { id: 'date', visible: true },
      { id: 'transactionType', visible: true },
      { id: 'value', visible: true },
      { id: 'actualSales', visible: true },
      { id: 'actualCost', visible: true },
      { id: 'postType', visible: true },
      { id: 'isInvoiced', visible: true },
      { id: 'batched', visible: true },
      { id: 'nominalCode', visible: true },
      { id: 'analysisName', visible: true },
    ],
  },
  {
    id: 'resource',
    name: 'Resource View',
    columns: [
      { id: 'projectCode', visible: true },
      { id: 'projectName', visible: true },
      { id: 'date', visible: true },
      { id: 'resourceName', visible: true },
      { id: 'resourceCode', visible: true },
      { id: 'employeeCode', visible: true },
      { id: 'quantity', visible: true },
      { id: 'price', visible: true },
      { id: 'value', visible: true },
      { id: 'transactionType', visible: true },
    ],
  },
  {
    id: 'billing',
    name: 'Billing View',
    columns: [
      { id: 'projectCode', visible: true },
      { id: 'projectName', visible: true },
      { id: 'accountName', visible: true },
      { id: 'date', visible: true },
      { id: 'value', visible: true },
      { id: 'uplift', visible: true },
      { id: 'upliftValue', visible: true },
      { id: 'valueWithUplift', visible: true },
      { id: 'isInvoiced', visible: true },
      { id: 'salesOrderNumber', visible: true },
    ],
  },
  {
    id: 'period',
    name: 'Period View',
    columns: [
      { id: 'projectCode', visible: true },
      { id: 'projectName', visible: true },
      { id: 'date', visible: true },
      { id: 'periodNumber', visible: true },
      { id: 'yearLabel', visible: true },
      { id: 'weekNo', visible: true },
      { id: 'value', visible: true },
      { id: 'transactionType', visible: true },
      { id: 'batched', visible: true },
    ],
  },
];

/**
 * Maps a ProjectTransactionDto (generic, from API) back to the CostingTransactionHeader
 * interface used by the existing rendering code. This avoids touching all the column
 * definitions and cell renderers.
 */
export function mapDtoToHeader(dto: ProjectTransactionDto): CostingTransactionHeader {
  return {
    projectCode: dto.projectCode ?? '',
    projectName: dto.projectName ?? '',
    date: dto.date ?? '',
    transactionType: (dto.transactionType ?? 'Timesheet') as TransactionType,
    origin: (dto.source ?? 'Costing') as TransactionOrigin,
    detail: dto.detail ?? '',
    headerRef: dto.reference ?? '',
    internalRef: dto.internalRef ?? 0,
    accountCode: dto.accountCode ?? '',
    accountName: dto.accountName ?? '',
    costCentreCode: dto.costCentreCode ?? '',
    costCentreName: dto.costCentreName ?? '',
    invoiceAccountCode: dto.invoiceAccountCode ?? '',
    invoiceAccountName: dto.invoiceAccountName ?? '',
    supplierAccountCode: dto.supplierAccountCode ?? '',
    resourceCode: dto.resourceCode ?? '',
    resourceName: dto.resourceName ?? '',
    employeeCode: dto.employeeCode ?? '',
    quantity: dto.quantity ?? 0,
    price: dto.price ?? 0,
    value: dto.value ?? 0,
    uplift: dto.uplift ?? 0,
    upliftValue: dto.upliftValue ?? 0,
    priceWithUplift: dto.priceWithUplift ?? 0,
    actualSales: dto.actualSales ?? 0,
    actualCost: dto.actualCost ?? 0,
    currencyCode: dto.currencyCode ?? '',
    postType: (dto.entryType ?? 'C') as PostType,
    projectStatus: dto.projectStatus ?? '',
    batched: dto.isBatched ?? false,
    isInvoiced: dto.isInvoiced ?? false,
    periodNumber: dto.periodNumber ?? 0,
    yearLabel: dto.yearLabel ?? '',
    weekNo: dto.weekNo ?? 0,
    costingSortKey: dto.sortKey ?? '',
    salesOrderNumber: dto.salesOrderNumber ?? 0,
    purchaseOrderNumber: dto.purchaseOrderNumber ?? '',
    batchRef: dto.batchRef ?? '',
    enteredBy: dto.enteredBy ?? '',
    dateEntered: dto.dateEntered ?? '',
    costingUserField1: dto.customField1 ?? '',
    costingUserField2: dto.customField2 ?? '',
    costingUserField3: dto.customField3 ?? '',
    costingUserField4: dto.customField4 ?? '',
    costingUserField5: dto.customField5 ?? '',
    costingUserField6: dto.customField6 ?? '',
    userDate1: dto.userDate1 ?? '',
    userDate2: dto.userDate2 ?? '',
    userChar1: dto.userChar1 ?? '',
    userChar2: dto.userChar2 ?? '',
    userChar3: dto.userChar3 ?? '',
    userChar4: dto.userChar4 ?? '',
    userFlag1: dto.userFlag1 ?? false,
    userFlag2: dto.userFlag2 ?? false,
    userNumber1: dto.userNumber1 ?? 0,
    userNumber2: dto.userNumber2 ?? 0,
    userKey1: dto.userKey1 ?? '',
    userKey2: dto.userKey2 ?? '',
    userKey3: dto.userKey3 ?? '',
    stockCode: dto.stockCode ?? '',
    stockName: dto.stockName ?? '',
    analysisCode: dto.analysisCode ?? '',
    analysisName: dto.analysisName ?? '',
    nominalCode: dto.nominalCode ?? '',
  };
}

// ── Sortable value accessor (needed for client-side demo sorting) ─────
function getTransactionColumnValue(columnId: string, tx: CostingTransactionHeader): string | number | boolean | undefined {
  switch (columnId) {
    case 'projectCode': return tx.projectCode;
    case 'projectName': return tx.projectName;
    case 'accountCode': return tx.accountCode;
    case 'accountName': return tx.accountName;
    case 'date': return tx.date;
    case 'transactionType': return tx.transactionType;
    case 'origin': return tx.origin;
    case 'resourceCode': return tx.resourceCode;
    case 'resourceName': return tx.resourceName;
    case 'employeeCode': return tx.employeeCode;
    case 'quantity': return tx.quantity;
    case 'price': return tx.price;
    case 'value': return tx.value;
    case 'uplift': return tx.uplift;
    case 'upliftValue': return tx.upliftValue;
    case 'priceWithUplift': return tx.priceWithUplift;
    case 'valueWithUplift': return tx.quantity * tx.priceWithUplift;
    case 'actualSales': return tx.actualSales;
    case 'actualCost': return tx.actualCost;
    case 'currencyCode': return tx.currencyCode;
    case 'postType': return tx.postType;
    case 'projectStatus': return tx.projectStatus;
    case 'batched': return tx.batched ? 1 : 0;
    case 'isInvoiced': return tx.isInvoiced ? 1 : 0;
    case 'periodNumber': return tx.periodNumber;
    case 'yearLabel': return tx.yearLabel;
    case 'weekNo': return tx.weekNo;
    case 'detail': return tx.detail;
    case 'headerRef': return tx.headerRef;
    case 'internalRef': return tx.internalRef;
    case 'costCentreCode': return tx.costCentreCode;
    case 'costCentreName': return tx.costCentreName;
    case 'invoiceAccountCode': return tx.invoiceAccountCode;
    case 'invoiceAccountName': return tx.invoiceAccountName;
    case 'supplierAccountCode': return tx.supplierAccountCode;
    case 'costingSortKey': return tx.costingSortKey;
    case 'salesOrderNumber': return tx.salesOrderNumber;
    case 'purchaseOrderNumber': return tx.purchaseOrderNumber;
    case 'batchRef': return tx.batchRef;
    case 'enteredBy': return tx.enteredBy;
    case 'dateEntered': return tx.dateEntered;
    case 'stockCode': return tx.stockCode;
    case 'stockName': return tx.stockName;
    case 'analysisCode': return tx.analysisCode;
    case 'analysisName': return tx.analysisName;
    case 'nominalCode': return tx.nominalCode;
    default: return undefined;
  }
}

export function ProjectTransactionsPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  
  // Live data state - holds the current page of transactions from the API
  const [liveTransactions, setLiveTransactions] = useState<CostingTransactionHeader[]>([]);
  
  const dataMode = getDataMode();
  const isLiveMode = dataMode === 'live';

  // Full filter options fetched once from existing API endpoints (projects, employees, clients)
  // so filter dropdowns show all available values, not just what's on the current page.
  const [allFilterOptions, setAllFilterOptions] = useState<{
    projectCodes: string[];
    projectNames: string[];
    resourceCodes: string[];
    resourceNames: string[];
    accountCodes: string[];
    accountNames: string[];
  } | null>(null);

  useEffect(() => {
    if (!isLiveMode) return;
    let cancelled = false;
    Promise.all([fetchProjects(), fetchEmployees(), fetchClients()])
      .then(([projects, employees, clients]) => {
        if (cancelled) return;
        const projectArr = Array.isArray(projects) ? projects : [];
        const employeeArr = Array.isArray(employees) ? employees : [];
        const clientArr = Array.isArray(clients) ? clients : [];
        setAllFilterOptions({
          projectCodes: [...new Set(projectArr.map((p: any) => p.code).filter(Boolean))].sort() as string[],
          projectNames: [...new Set(projectArr.map((p: any) => p.name).filter(Boolean))].sort() as string[],
          resourceCodes: [...new Set(employeeArr.map((e: any) => e.externalUserId).filter(Boolean))].sort() as string[],
          resourceNames: [...new Set(employeeArr.map((e: any) => e.name).filter(Boolean))].sort() as string[],
          accountCodes: [...new Set(clientArr.map((c: any) => c.code).filter(Boolean))].sort() as string[],
          accountNames: [...new Set(clientArr.map((c: any) => c.name).filter(Boolean))].sort() as string[],
        });
      })
      .catch((err) => {
        if (!cancelled) console.warn('[TransactionFilters] Error loading filter options:', err);
      });
    return () => { cancelled = true; };
  }, [isLiveMode]);

  // Column configurator state
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);

  // Column preferences - persisted per user via useColumnPreferences
  const { columns, updateColumns } = useColumnPreferences({
    storageKey: 'project-transactions-column-preferences',
    defaultColumns: COLUMN_DEFINITIONS,
  });
  
  // Read URL params so callers can pre-filter by projectCode and/or entryType.
  // e.g. /project-transactions?projectCode=PROJ001&entryType=C
  const location = useLocation();
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlProjectCode = urlParams.get('projectCode') ?? '';
  const urlEntryType = urlParams.get('entryType') ?? '';
  const urlAnalysisCodes = urlParams.get('analysisCodes') ?? '';

  // Filter state — seeded from URL params on first mount
  const [filters, setFilters] = useState<FilterState>(() => ({
    ...EMPTY_TRANSACTION_FILTERS,
    ...(urlProjectCode ? { projectCodes: [urlProjectCode] } : {}),
    ...(urlEntryType ? { postType: urlEntryType as FilterState['postType'] } : {}),
    ...(urlAnalysisCodes ? { analysisCodes: urlAnalysisCodes.split(',').filter(Boolean) } : {}),
  }));

  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // Multi-column sort state: ordered array where first entry = primary sort, second = secondary, etc.
  // Click cycles: none -> asc -> desc -> remove
  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>([]);

  // ── Pagination ────────────────────────────────────────────────────────
  // The usePagination hook manages pageSize and the demo-mode page number.
  // For live mode we track a separate server page with cursor-based navigation.
  const {
    currentPage: demoCurrentPage,
    pageSize,
    setPage: setDemoPage,
    setPageSize,
  } = usePagination({ defaultPageSize: 25, persistInUrl: false });

  // Server-side cursor-based pagination state (live mode only)
  const [liveCurrentPage, setLiveCurrentPage] = useState(1);
  const [liveHasNextPage, setLiveHasNextPage] = useState(false);
  // The highest page number we've successfully visited (so we can show page numbers up to here).
  const [liveMaxPage, setLiveMaxPage] = useState(1);
  // Stores the endCursor returned by each page so we can navigate back.
  // Index 0 = null (first page has no cursor), index N = cursor for page N+1.
  const pageCursorsRef = useRef<(string | null)[]>([null]);

  // Keep filters in sync if the user navigates to a new pre-filtered URL
  // (e.g. clicking two different drill-throughs without leaving the page)
  useEffect(() => {
    setFilters({
      ...EMPTY_TRANSACTION_FILTERS,
      ...(urlProjectCode ? { projectCodes: [urlProjectCode] } : {}),
      ...(urlEntryType ? { postType: urlEntryType as FilterState['postType'] } : {}),
      ...(urlAnalysisCodes ? { analysisCodes: urlAnalysisCodes.split(',').filter(Boolean) } : {}),
    });
    // Reset to first page whenever pre-filter changes
    setDemoPage(1);
    setLiveCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlProjectCode, urlEntryType]);

  // Debounced search term for server-side queries — avoids re-fetching on every keystroke.
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(filters.searchTerm);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchTerm(filters.searchTerm);
    }, 300);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [filters.searchTerm]);

  // Derive a stable key for the server-side filter params that should trigger a re-fetch.
  const serverFilterKey = useMemo(() => JSON.stringify({
    pc: filters.projectCodes,
    tt: filters.transactionTypes,
    o: filters.origins,
    pt: filters.postType,
    df: filters.dateFrom,
    dt: filters.dateTo,
    b: filters.batched,
    inv: filters.isInvoiced,
    st: debouncedSearchTerm,
  }), [filters.projectCodes, filters.transactionTypes, filters.origins, filters.postType,
       filters.dateFrom, filters.dateTo, filters.batched, filters.isInvoiced, debouncedSearchTerm]);

  const serverSortKey = useMemo(() => JSON.stringify(
    columnSorts.map(s => ({ id: s.columnId, dir: s.direction }))
  ), [columnSorts]);

  // Build the query body from the current state
  const buildQueryBody = useCallback((page: number): ProjectTransactionQueryBody => {
    const cursor = pageCursorsRef.current[page - 1] ?? null;

    // Build sort - fall back to TransactionId descending if no column sorts
    const sort = columnSorts.length > 0
      ? columnSorts.map(s => ({
          field: SORT_FIELD_MAP[s.columnId] ?? s.columnId,
          order: s.direction === 'asc' ? 'Ascending' : 'Descending',
        }))
      : [{ field: 'TransactionId', order: 'Descending' }];

    const body: ProjectTransactionQueryBody = {
      first: pageSize,
      after: cursor,
      sort,
    };

    // Structured filters sent server-side
    if (filters.projectCodes.length > 0) body.projectCodes = [...filters.projectCodes];
    if (filters.transactionTypes.length > 0) body.transactionTypes = [...filters.transactionTypes];
    if (filters.origins.length > 0) body.sources = [...filters.origins];
    if (filters.postType !== 'all') body.entryType = filters.postType;
    if (filters.batched !== 'all') body.isBatched = filters.batched === 'true';
    if (filters.isInvoiced !== 'all') body.isInvoiced = filters.isInvoiced === 'true';
    if (filters.dateFrom) body.dateFrom = filters.dateFrom;
    if (filters.dateTo) body.dateTo = filters.dateTo;
    if (debouncedSearchTerm) body.searchTerm = debouncedSearchTerm;
    if (filters.analysisCodes.length > 0) body.analysisCodes = [...filters.analysisCodes];

    return body;
  }, [pageSize, filters, columnSorts, debouncedSearchTerm]);

  // Core fetch function - fetches a specific page of live data
  const loadLivePage = useCallback(async (page: number) => {
    setLoading(true);
    setError(null);

    try {
      const body = buildQueryBody(page);
      const response = await queryProjectTransactions(body);

      if (response.errorMessage) {
        setError(response.errorMessage);
        setLiveTransactions([]);
        setLiveHasNextPage(false);
      } else {
        setLiveTransactions(response.items.map(mapDtoToHeader));
        setLiveHasNextPage(response.pageInfo?.hasNextPage ?? false);
        // Track the furthest page we've reached
        setLiveMaxPage(prev => Math.max(prev, page));
        // Cache the endCursor so we can navigate forward later
        if (response.pageInfo?.endCursor) {
          pageCursorsRef.current[page] = response.pageInfo.endCursor;
        }
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message :
        (err && typeof err === 'object' && 'message' in err) ? String((err as Record<string, unknown>).message) : 'Failed to load transactions';
      setError(message);
      setLiveTransactions([]);
      setLiveHasNextPage(false);
    } finally {
      setLoading(false);
    }
  }, [buildQueryBody]);

  // ── Server-side KPI totals ───────────────────────────────────────────
  const [serverTotals, setServerTotals] = useState<ProjectTransactionTotalsResponse | null>(null);

  const fetchTotals = useCallback(async () => {
    if (!isLiveMode) return;
    try {
      // Build the same filter body as the paginated query, but without pagination/sort
      const body: ProjectTransactionQueryBody = { first: 1 };
      if (filters.projectCodes.length > 0) body.projectCodes = [...filters.projectCodes];
      if (filters.transactionTypes.length > 0) body.transactionTypes = [...filters.transactionTypes];
      if (filters.origins.length > 0) body.sources = [...filters.origins];
      if (filters.postType !== 'all') body.entryType = filters.postType;
      if (filters.batched !== 'all') body.isBatched = filters.batched === 'true';
      if (filters.isInvoiced !== 'all') body.isInvoiced = filters.isInvoiced === 'true';
      if (filters.dateFrom) body.dateFrom = filters.dateFrom;
      if (filters.dateTo) body.dateTo = filters.dateTo;
      if (debouncedSearchTerm) body.searchTerm = debouncedSearchTerm;

      const totals = await queryProjectTransactionTotals(body);
      if (!totals.errorMessage) {
        setServerTotals(totals);
      } else {
        console.warn('[Transactions] Totals query returned error, falling back to client-side KPIs:', totals.errorMessage);
        setServerTotals(null);
      }
    } catch (err) {
      console.warn('[Transactions] Totals query failed, falling back to client-side KPIs:', err);
      setServerTotals(null);
    }
  }, [isLiveMode, filters, debouncedSearchTerm]);

  // ── Server-side grouped totals ──────────────────────────────────────
  const [serverGroupedData, setServerGroupedData] = useState<ProjectTransactionGroupedTotalsResponse | null>(null);
  const [groupedLoading, setGroupedLoading] = useState(false);

  const fetchGroupedTotals = useCallback(async (groupByField: TransactionGroupById | null) => {
    if (!isLiveMode || !groupByField) {
      setServerGroupedData(null);
      return;
    }
    setGroupedLoading(true);
    try {
      const body: ProjectTransactionQueryBody & { groupBy: string } = { first: 1, groupBy: groupByField };
      if (filters.projectCodes.length > 0) body.projectCodes = [...filters.projectCodes];
      if (filters.transactionTypes.length > 0) body.transactionTypes = [...filters.transactionTypes];
      if (filters.origins.length > 0) body.sources = [...filters.origins];
      if (filters.postType !== 'all') body.entryType = filters.postType;
      if (filters.batched !== 'all') body.isBatched = filters.batched === 'true';
      if (filters.isInvoiced !== 'all') body.isInvoiced = filters.isInvoiced === 'true';
      if (filters.dateFrom) body.dateFrom = filters.dateFrom;
      if (filters.dateTo) body.dateTo = filters.dateTo;
      if (debouncedSearchTerm) body.searchTerm = debouncedSearchTerm;
      if (filters.analysisCodes.length > 0) body.analysisCodes = [...filters.analysisCodes];

      const result = await queryProjectTransactionGroupedTotals(body);
      if (!result.errorMessage) {
        setServerGroupedData(result);
      } else {
        console.warn('[Transactions] Grouped totals query returned error:', result.errorMessage);
        setServerGroupedData(null);
      }
    } catch (err) {
      console.warn('[Transactions] Grouped totals query failed:', err);
      setServerGroupedData(null);
    } finally {
      setGroupedLoading(false);
    }
  }, [isLiveMode, filters, debouncedSearchTerm]);

  // Initial load + reset when server filters, sort, or page size change
  useEffect(() => {
    if (!isLiveMode) return;
    // Reset cursor history and go to page 1
    pageCursorsRef.current = [null];
    setLiveCurrentPage(1);
    setLiveMaxPage(1);
    loadLivePage(1);
    // Fire totals request in parallel with the paginated query
    fetchTotals();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLiveMode, serverFilterKey, serverSortKey, pageSize]); // loadLivePage/fetchTotals intentionally omitted to avoid double-fire

  // Navigation helpers (live mode)
  const goToLiveNextPage = useCallback(() => {
    if (!liveHasNextPage) return;
    const newPage = liveCurrentPage + 1;
    setLiveCurrentPage(newPage);
    loadLivePage(newPage);
  }, [liveHasNextPage, liveCurrentPage, loadLivePage]);

  const goToLivePrevPage = useCallback(() => {
    if (liveCurrentPage <= 1) return;
    const newPage = liveCurrentPage - 1;
    setLiveCurrentPage(newPage);
    loadLivePage(newPage);
  }, [liveCurrentPage, loadLivePage]);

  const goToLiveFirstPage = useCallback(() => {
    if (liveCurrentPage === 1) return;
    setLiveCurrentPage(1);
    loadLivePage(1);
  }, [liveCurrentPage, loadLivePage]);

  // Jump to a page. Pages within the cursor cache are loaded directly.
  // Pages beyond the cache are clamped to the next unexplored page.
  const goToLivePage = useCallback((page: number) => {
    if (page === liveCurrentPage) return;
    if (page < 1) return;
    const maxJumpable = liveHasNextPage ? liveMaxPage + 1 : liveMaxPage;
    const targetPage = Math.min(page, maxJumpable);
    if (targetPage === liveCurrentPage) return;
    setLiveCurrentPage(targetPage);
    loadLivePage(targetPage);
  }, [liveCurrentPage, liveMaxPage, liveHasNextPage, loadLivePage]);

  // Retry (for error banner)
  const retryLoad = useCallback(() => {
    if (isLiveMode) {
      loadLivePage(liveCurrentPage);
    }
  }, [isLiveMode, liveCurrentPage, loadLivePage]);

  // ── Data pipeline ─────────────────────────────────────────────────────
  // In live mode: liveTransactions already contains one server page (filtered & sorted server-side).
  //   We still apply text-search filters client-side on the current page.
  // In demo mode: MOCK_TRANSACTIONS is the full dataset - all filtering, sorting, and pagination
  //   happens client-side as before.
  const sourceTransactions = isLiveMode ? liveTransactions : MOCK_TRANSACTIONS;

  // Client-side filters - in live mode these apply to the current page only.
  // In demo mode they apply to the full mock dataset.
  // Server-side filters (transactionTypes, origins, postType, batched, isInvoiced, dates)
  // are already handled by the API in live mode, but we still apply them in demo mode.
  const filteredTransactions = useMemo(() => {
    return sourceTransactions.filter(tx => {
      // In live mode, searchTerm is sent to the server — skip client-side filtering.
      // In demo mode, filter client-side.
      if (!isLiveMode && filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        const searchable = [
          tx.projectCode, tx.projectName, tx.accountCode, tx.accountName,
          tx.resourceCode, tx.resourceName, tx.detail, tx.headerRef,
        ].join(' ').toLowerCase();
        if (!searchable.includes(term)) return false;
      }
      // Multi-select text filters (client-side on current page; projectCodes also sent server-side)
      if (filters.projectCodes.length > 0 && !filters.projectCodes.includes(tx.projectCode)) return false;
      if (filters.projectNames.length > 0 && !filters.projectNames.includes(tx.projectName)) return false;
      if (filters.accountCodes.length > 0 && !filters.accountCodes.includes(tx.accountCode)) return false;
      if (filters.accountNames.length > 0 && !filters.accountNames.includes(tx.accountName)) return false;
      if (filters.resourceCodes.length > 0 && !filters.resourceCodes.includes(tx.resourceCode)) return false;
      if (filters.resourceNames.length > 0 && !filters.resourceNames.includes(tx.resourceName)) return false;

      // Structured filters - in demo mode apply client-side; in live mode the server already filtered these
      if (!isLiveMode) {
        if (filters.transactionTypes.length > 0 && !filters.transactionTypes.includes(tx.transactionType)) return false;
        if (filters.origins.length > 0 && !filters.origins.includes(tx.origin)) return false;
        if (filters.postType !== 'all' && tx.postType !== filters.postType) return false;
        if (filters.dateFrom && tx.date < filters.dateFrom) return false;
        if (filters.dateTo && tx.date > filters.dateTo) return false;
        if (filters.batched !== 'all' && tx.batched !== (filters.batched === 'true')) return false;
        if (filters.isInvoiced !== 'all' && tx.isInvoiced !== (filters.isInvoiced === 'true')) return false;
      }
      return true;
    });
  }, [filters, sourceTransactions, isLiveMode]);

  // ── Excel export handler ──────────────────────────────────────────────
  const handleExportExcel = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    toast.info('Export started — your download will begin shortly.');

    try {
      if (!isLiveMode) {
        await exportTransactionsToExcel(filteredTransactions, columns);
        toast.success('Export complete — file downloaded.');
        return;
      }

      const allTransactions = await fetchAllTransactionPages((cursor) => {
        const sort = columnSorts.length > 0
          ? columnSorts.map(s => ({
              field: SORT_FIELD_MAP[s.columnId] ?? s.columnId,
              order: s.direction === 'asc' ? 'Ascending' : 'Descending',
            }))
          : [{ field: 'TransactionId', order: 'Descending' }];

        const body: ProjectTransactionQueryBody = {
          first: 1000,
          after: cursor,
          sort,
        };
        if (filters.projectCodes.length > 0) body.projectCodes = [...filters.projectCodes];
        if (filters.transactionTypes.length > 0) body.transactionTypes = [...filters.transactionTypes];
        if (filters.origins.length > 0) body.sources = [...filters.origins];
        if (filters.postType !== 'all') body.entryType = filters.postType;
        if (filters.batched !== 'all') body.isBatched = filters.batched === 'true';
        if (filters.isInvoiced !== 'all') body.isInvoiced = filters.isInvoiced === 'true';
        if (filters.dateFrom) body.dateFrom = filters.dateFrom;
        if (filters.dateTo) body.dateTo = filters.dateTo;
        if (debouncedSearchTerm) body.searchTerm = debouncedSearchTerm;
        return body;
      });

      await exportTransactionsToExcel(allTransactions, columns);
      toast.success(`Export complete — ${allTransactions.length} transactions downloaded.`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, isLiveMode, filteredTransactions, columns, columnSorts, filters, debouncedSearchTerm]);

  // Total pages derived from the server-side totalCount (available from the totals endpoint).
  // Falls back to a minimum estimate when totals haven't loaded yet.
  const liveTotalPages = useMemo(() => {
    if (serverTotals?.totalCount != null && serverTotals.totalCount > 0) {
      return Math.ceil(serverTotals.totalCount / pageSize);
    }
    // Fallback: best guess from explored pages
    return liveHasNextPage ? liveCurrentPage + 1 : liveCurrentPage;
  }, [serverTotals, pageSize, liveHasNextPage, liveCurrentPage]);

  // Pagination metadata
  const pagination = useMemo(() => {
    if (isLiveMode) {
      const count = filteredTransactions.length;
      return {
        currentPage: liveCurrentPage,
        pageSize,
        totalItems: serverTotals?.totalCount ?? count,
        totalPages: liveTotalPages,
        hasNextPage: liveHasNextPage,
        hasPreviousPage: liveCurrentPage > 1,
        startIndex: 0,
        endIndex: count,
      };
    }
    return calculatePagination(filteredTransactions.length, demoCurrentPage, pageSize);
  }, [isLiveMode, filteredTransactions.length, liveCurrentPage, liveHasNextPage, pageSize, demoCurrentPage, liveTotalPages, serverTotals]);

  // Get sorted + paginated data
  // In live mode: data is already sorted & paginated by the server. filteredTransactions
  //   contains only the current page (possibly narrowed by client-side text search).
  // In demo mode: sort client-side and slice to the current page.
  const paginatedData = useMemo(() => {
    if (isLiveMode) {
      // Server already sorted and paginated - just return the (client-filtered) page
      return filteredTransactions;
    }

    // Demo mode: client-side sort + paginate
    if (columnSorts.length === 0) {
      return filteredTransactions.slice(pagination.startIndex, pagination.endIndex);
    }

    const sorted = [...filteredTransactions].sort((a, b) => {
      for (const { columnId, direction } of columnSorts) {
        const aVal = getTransactionColumnValue(columnId, a);
        const bVal = getTransactionColumnValue(columnId, b);

        // Handle undefined values - push them to the end
        if (aVal === undefined && bVal === undefined) continue;
        if (aVal === undefined) return 1;
        if (bVal === undefined) return -1;

        let cmp = 0;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          cmp = aVal - bVal;
        } else {
          cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base' });
        }

        if (cmp !== 0) {
          return direction === 'asc' ? cmp : -cmp;
        }
        // If equal, fall through to next sort column
      }
      return 0;
    });

    const start = (demoCurrentPage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [isLiveMode, filteredTransactions, columnSorts, pagination.startIndex, pagination.endIndex, demoCurrentPage, pageSize]);

  // Calculate KPIs — prefer server-side totals in live mode for accuracy across all pages
  const kpis = useMemo(() => {
    if (isLiveMode && serverTotals) {
      return {
        totalTransactions: serverTotals.totalCount,
        salesActual: serverTotals.totalSales,
        costActual: serverTotals.totalCost,
        netActual: serverTotals.netTotal,
      };
    }
    // Demo mode fallback — compute from local data
    // Exclude TaxJournal from sales (VAT is not revenue, consistent with SalesActual roll-up)
    const totalTransactions = filteredTransactions.length;
    const salesActual = filteredTransactions
      .filter(tx => tx.postType === 'S' && tx.transactionType !== 'TaxJournal')
      .reduce((sum, tx) => sum + tx.value, 0);
    const costActual = filteredTransactions
      .filter(tx => tx.postType === 'C')
      .reduce((sum, tx) => sum + tx.value, 0);
    const netActual = salesActual - costActual;
    return { totalTransactions, salesActual, costActual, netActual };
  }, [isLiveMode, serverTotals, filteredTransactions]);
  
  // Derive which KPI filter is currently active
  const activeKpiFilter = useMemo(() => {
    if (filters.postType === 'S') return 'sales';
    if (filters.postType === 'C') return 'costs';
    return null;
  }, [filters.postType]);

  // ── Grouping ─────────────────────────────────────────────────────────
  const [groupBy, setGroupBy] = useState<TransactionGroupById | null>(null);
  // Track which groups are collapsed — key is the group label
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const expandAllGroups = () => setCollapsedGroups(new Set());
  const groupedDataRef = useRef<{ key: string }[] | null>(null);
  const collapseAllGroups = () => {
    if (groupedDataRef.current) {
      setCollapsedGroups(new Set(groupedDataRef.current.map(g => g.key)));
    }
  };

  // Group key accessor for a single groupBy field
  const getSingleGroupKey = useCallback((tx: CostingTransactionHeader, by: TransactionGroupById): string => {
    switch (by) {
      case 'project':         return `${tx.projectCode} – ${tx.projectName}`;
      case 'transactionType': return tx.transactionType;
      case 'period':          return tx.yearLabel && tx.periodNumber
        ? `${tx.yearLabel} / Period ${String(tx.periodNumber).padStart(2, '0')}`
        : tx.yearLabel || 'Unknown';
      case 'costCentre':      return tx.costCentreName
        ? `${tx.costCentreCode} – ${tx.costCentreName}`
        : tx.costCentreCode || 'No Cost Centre';
      case 'employee':        return tx.resourceName || tx.employeeCode || 'Unknown';
      default:                return '';
    }
  }, []);

  // Build group key for single groupBy field
  const getCompositeGroupKey = useCallback((tx: CostingTransactionHeader): string => {
    if (!groupBy) return '';
    return getSingleGroupKey(tx, groupBy);
  }, [groupBy, getSingleGroupKey]);

  interface TransactionGroup {
    key: string;
    label: string;
    rows: CostingTransactionHeader[];
    totalSales: number;
    totalCost: number;
    net: number;
    count?: number;
  }

  // Compute grouped data — server-side in live mode, client-side in demo mode
  const groupedData = useMemo((): TransactionGroup[] | null => {
    if (!groupBy) return null;

    // Live mode: use server-side grouped totals
    if (isLiveMode && serverGroupedData?.groups?.length) {
      return serverGroupedData.groups.map(g => ({
        key: g.key,
        label: g.key,
        rows: [],
        totalSales: g.totalSales,
        totalCost: g.totalCost,
        net: g.net,
        count: g.count,
      }));
    }

    // Demo mode fallback: client-side grouping
    const map = new Map<string, CostingTransactionHeader[]>();
    for (const tx of filteredTransactions) {
      const key = getCompositeGroupKey(tx);
      const arr = map.get(key);
      if (arr) arr.push(tx);
      else map.set(key, [tx]);
    }

    const entries = [...map.entries()].sort(([a], [b]) => a.localeCompare(b));

    return entries.map(([key, rows]) => {
      const totalSales = rows.filter(tx => tx.postType === 'S' && tx.transactionType !== 'TaxJournal').reduce((s, tx) => s + tx.value, 0);
      const totalCost  = rows.filter(tx => tx.postType === 'C').reduce((s, tx) => s + tx.value, 0);
      return { key, label: key, rows, totalSales, totalCost, net: totalSales - totalCost };
    });
  }, [groupBy, isLiveMode, serverGroupedData, filteredTransactions, getCompositeGroupKey]);

  // Keep groupedDataRef in sync so collapseAllGroups can access latest keys
  groupedDataRef.current = groupedData;

  // Reset collapsed state when group-by changes
  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [groupBy]);

  // Fetch server-side grouped totals when groupBy or filters change
  useEffect(() => {
    fetchGroupedTotals(groupBy);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy, serverFilterKey]); // fetchGroupedTotals intentionally omitted to avoid double-fire
  
  // Toggle KPI filter (Sales or Cost) - click again to unfilter
  const applyKpiFilter = (type: 'sales' | 'costs') => {
    const postTypeForFilter = type === 'sales' ? 'S' : 'C';
    setFilters(prev => ({
      ...prev,
      postType: prev.postType === postTypeForFilter ? 'all' : postTypeForFilter,
    }));
  };
  
  // Clear all filters
  const clearFilters = () => {
    setFilters({ ...EMPTY_TRANSACTION_FILTERS });
  };

  // Build known values for filter dropdowns.
  // In live mode: use the full project/resource/client lists fetched from the Projects,
  // Employees, and Clients endpoints so all available values appear in the dropdowns.
  // In demo mode: derive from the mock data.
  const knownFilterValues = useMemo(() => {
    const txs = isLiveMode ? liveTransactions : MOCK_TRANSACTIONS;
    const pageFallback = {
      projectCodes: [...new Set(txs.map(tx => tx.projectCode).filter(Boolean))].sort(),
      projectNames: [...new Set(txs.map(tx => tx.projectName).filter(Boolean))].sort(),
      accountCodes: [...new Set(txs.map(tx => tx.accountCode).filter(Boolean))].sort(),
      accountNames: [...new Set(txs.map(tx => tx.accountName).filter(Boolean))].sort(),
      resourceCodes: [...new Set(txs.map(tx => tx.resourceCode).filter(Boolean))].sort(),
      resourceNames: [...new Set(txs.map(tx => tx.resourceName).filter(Boolean))].sort(),
    };
    if (!isLiveMode || !allFilterOptions) return pageFallback;
    return {
      projectCodes: allFilterOptions.projectCodes,
      projectNames: allFilterOptions.projectNames,
      accountCodes: allFilterOptions.accountCodes,
      accountNames: allFilterOptions.accountNames,
      resourceCodes: allFilterOptions.resourceCodes,
      resourceNames: allFilterOptions.resourceNames,
    };
  }, [isLiveMode, allFilterOptions, liveTransactions]);
  
  // Count active filters (excluding searchTerm which has its own clear button)
  const activeFilterCount = useMemo(() => countActiveTransactionFilters(filters), [filters]);

  const hasActiveFilters = filters.searchTerm !== '' || activeFilterCount > 0;

  // ── Pagination content (passed to TransactionsGrid) ─────────────────
  const paginationMeta: PaginationMetadata = isLiveMode
    ? {
        currentPage: liveCurrentPage,
        pageSize,
        totalItems: liveHasNextPage ? liveCurrentPage * pageSize + 1 : (liveCurrentPage - 1) * pageSize + filteredTransactions.length,
        totalPages: liveTotalPages,
        hasNextPage: liveHasNextPage,
        hasPreviousPage: liveCurrentPage > 1,
        startIndex: (liveCurrentPage - 1) * pageSize,
        endIndex: (liveCurrentPage - 1) * pageSize + filteredTransactions.length,
      }
    : {
        currentPage: demoCurrentPage,
        pageSize,
        totalItems: pagination.totalItems,
        totalPages: pagination.totalPages,
        hasNextPage: pagination.hasNextPage,
        hasPreviousPage: pagination.hasPreviousPage,
        startIndex: pagination.startIndex,
        endIndex: pagination.endIndex,
      };

  const paginationContent = (filteredTransactions.length > 0 || (isLiveMode && liveCurrentPage > 1)) ? (
    <PaginationControls
      pagination={paginationMeta}
      onPageChange={isLiveMode ? goToLivePage : setDemoPage}
      onPageSizeChange={(size) => setPageSize(size)}
      showPageSizeSelector={true}
    />
  ) : null;

  return (
    <div className="space-y-6">
      <PageHeaderBlock
        title="Project Transactions"
        subtitle="Comprehensive costing transaction reporting with advanced filtering"
        size={20}
      />
      
      {/* Error banner for live mode API failures */}
      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive flex items-center justify-between">
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={() => { setError(null); retryLoad(); }}>
            Retry
          </Button>
        </div>
      )}
      
      {/* KPI Summary */}
      <div className="grid gap-4 md:grid-cols-4">
        {/* Total Transactions */}
        <CompactKpiCard
          title="Total Transactions"
          value={kpis.totalTransactions}
          description="All transaction types"
          icon={Hash}
          accentVariant="info"
          className="shadow-none"
        />

        {/* Sales Actual - Clickable */}
        <div
          className={cn(
            'cursor-pointer rounded-xl',
            activeKpiFilter === 'sales' && 'ring-2 ring-offset-2 ring-success-400 dark:ring-success-600',
          )}
          onClick={() => applyKpiFilter('sales')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); applyKpiFilter('sales'); } }}
          title={activeKpiFilter === 'sales' ? 'Click to remove filter' : 'Click to filter sales transactions'}
        >
          <CompactKpiCard
            title="Sales Actual"
            value={<SensitiveData>{formatCurrency(kpis.salesActual)}</SensitiveData>}
            description={activeKpiFilter === 'sales' ? 'Click to remove filter' : 'Click to filter sales'}
            icon={TrendingUp}
            accentVariant="success"
            className="shadow-none h-full"
          />
        </div>

        {/* Cost Actual - Clickable */}
        <div
          className={cn(
            'cursor-pointer rounded-xl',
            activeKpiFilter === 'costs' && 'ring-2 ring-offset-2 ring-warning-400 dark:ring-warning-600',
          )}
          onClick={() => applyKpiFilter('costs')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); applyKpiFilter('costs'); } }}
          title={activeKpiFilter === 'costs' ? 'Click to remove filter' : 'Click to filter cost transactions'}
        >
          <CompactKpiCard
            title="Cost Actual"
            value={<SensitiveData>{formatCurrency(kpis.costActual)}</SensitiveData>}
            description={activeKpiFilter === 'costs' ? 'Click to remove filter' : 'Click to filter costs'}
            icon={TrendingDown}
            accentVariant="info"
            className="shadow-none h-full"
          />
        </div>

        {/* Net Actual */}
        <CompactKpiCard
          title="Net Actual"
          value={
            <SensitiveData>
              {kpis.netActual >= 0 ? '+' : ''}
              {formatCurrency(kpis.netActual)}
            </SensitiveData>
          }
          description="Sales minus Cost"
          icon={ArrowUpDown}
          accentVariant={kpis.netActual >= 0 ? 'success' : 'error'}
          className="shadow-none"
        />
      </div>
      
      {/* Transactions Table */}
      <Card data-tour="transactions-table">
        <CardContent className="p-0">
          {/* Toolbar */}
          <div className="flex gap-2 flex-wrap p-6 pb-4" data-tour="transactions-filters">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by project, account, resource or reference..."
                  value={filters.searchTerm}
                  onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                  className="pl-9 pr-9"
                />
                {filters.searchTerm && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setFilters(prev => ({ ...prev, searchTerm: '' }))}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>

            {/* Group By popover */}
            <TransactionGroupByFilter
              groupBy={groupBy}
              onGroupByChange={setGroupBy}
            />

            <Button
              variant="outline"
              onClick={() => setFilterSheetOpen(true)}
              className="h-9 w-9 p-0 relative"
            >
              <Filter className="h-4 w-4" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 text-[10px] font-semibold text-primary-foreground min-w-[18px] h-[18px] rounded-full bg-primary inline-flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </Button>
            {hasActiveFilters && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="text-muted-foreground h-9"
              >
                <X className="h-4 w-4 mr-2" />
                Clear Filters
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setColumnConfigOpen(true)}
              className="h-9 w-9 p-0"
            >
              <Settings2 className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9"
              onClick={handleExportExcel}
              disabled={isExporting}
              title="Export to Excel"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Grouped view */}
          {groupBy !== null && (groupedData || groupedLoading) ? (
            <div className="border-t">
              {groupedLoading && !groupedData && (
                <div className="flex items-center justify-center py-10 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Loading grouped totals…
                </div>
              )}
              {groupedData && (
              <>
              {isLiveMode && serverGroupedData && (
                <div className="px-4 py-2 text-xs text-muted-foreground bg-muted/30 border-b">
                  Showing aggregated totals across all pages
                </div>
              )}
              {/* Info bar — groups count + expand/collapse all (right-aligned) */}
              {!(isLiveMode && serverGroupedData) && (
              <div className="flex items-center justify-end gap-3 px-6 py-2">
                <p className="text-xs text-muted-foreground">
                  {groupedData.length} {groupedData.length === 1 ? 'group' : 'groups'} ·{' '}
                  {filteredTransactions.length} transactions total
                </p>
                <div className="flex gap-3 text-xs">
                  <button
                    type="button"
                    onClick={expandAllGroups}
                    className="text-primary hover:underline"
                  >
                    Expand all
                  </button>
                  <button
                    type="button"
                    onClick={collapseAllGroups}
                    className="text-primary hover:underline"
                  >
                    Collapse all
                  </button>
                </div>
              </div>
              )}

              {/* Group sections */}
              {groupedData.length === 0 ? (
                <div className="text-center text-muted-foreground py-10 text-sm">
                  No transactions found
                </div>
              ) : (
                groupedData.map((group) => {
                  const isCollapsed = collapsedGroups.has(group.key);
                  const isServerGrouped = isLiveMode && !!serverGroupedData;
                  return (
                    <div key={group.key} className="border-t">
                      {/* Group header row */}
                      <div
                        className={cn(
                          "flex items-center gap-3 px-4 py-2.5 bg-muted/50 hover:bg-muted select-none",
                          !isServerGrouped && "cursor-pointer",
                        )}
                        onClick={() => !isServerGrouped && toggleGroup(group.key)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') !isServerGrouped && toggleGroup(group.key); }}
                      >
                        {!isServerGrouped && (
                        <ChevronDownIcon
                          className={cn(
                            'h-4 w-4 text-muted-foreground transition-transform duration-200 shrink-0',
                            isCollapsed && '-rotate-90',
                          )}
                        />
                        )}
                        <span className="text-sm font-semibold text-foreground flex-1">{group.label}</span>
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {group.count ?? group.rows.length} {(group.count ?? group.rows.length) === 1 ? 'transaction' : 'transactions'}
                        </span>
                        <span className="text-xs tabular-nums font-medium text-muted-foreground w-24 text-right">
                          <SensitiveData>Sales: {formatCurrency(group.totalSales)}</SensitiveData>
                        </span>
                        <span className="text-xs tabular-nums font-medium text-muted-foreground w-24 text-right">
                          <SensitiveData>Cost: {formatCurrency(group.totalCost)}</SensitiveData>
                        </span>
                        <span className="text-xs tabular-nums font-semibold text-foreground w-24 text-right">
                          <SensitiveData>Net: {group.net >= 0 ? '+' : ''}{formatCurrency(group.net)}</SensitiveData>
                        </span>
                      </div>

                      {/* Full TransactionsGrid for this group's rows */}
                      {!isServerGrouped && !isCollapsed && (
                        <TransactionsGrid
                          key={`group-${group.key}`}
                          data={group.rows}
                          columnDefinitions={columns}
                          columnSorts={columnSorts}
                          onColumnSortChange={setColumnSorts}
                          loading={false}
                          pageSize={group.rows.length}
                          hasActiveFilters={false}
                          className="rounded-none border-0 border-t"
                        />
                      )}
                    </div>
                  );
                })
              )}
              </>
              )}
            </div>
          ) : (
          /* Standard paginated grid (no grouping) */
          <TransactionsGrid
            key={isLiveMode ? `live-${liveCurrentPage}` : `demo-${demoCurrentPage}`}
            data={paginatedData}
            columnDefinitions={columns}
            columnSorts={columnSorts}
            onColumnSortChange={setColumnSorts}
            loading={loading}
            pageSize={pageSize}
            hasActiveFilters={hasActiveFilters}
            paginationContent={paginationContent}
            className="rounded-none border-0 border-t"
          />
          )}
        </CardContent>
      </Card>
      
      {/* Column Configurator */}
      <ColumnConfiguratorDrawer
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onSave={updateColumns}
        presets={COLUMN_PRESETS}
        defaultColumns={COLUMN_DEFINITIONS}
        pageKey="project-transactions"
      />

      {/* Transaction Filter Sheet */}
      <TransactionFilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        filters={filters}
        onApply={setFilters}
        onClear={clearFilters}
        knownValues={knownFilterValues}
      />
    </div>
  );
}
